import { useNavigate } from "react-router-dom";
import { X, Trash2, Plus, Minus, ShoppingBag } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { money } from "@/lib/format";

const MEMBER_ROLES = ["community", "enhancer", "staff", "council", "admin"];

const memberPrice = (item, isMember) => {
  const base = Number(item.price || 0) + Number(item.price_delta || 0);
  return isMember && item.member_discount_pct > 0
    ? base * (1 - item.member_discount_pct / 100)
    : base;
};

export default function CartDrawer() {
  const { items, setQty, remove, open, setOpen } = useCart();
  const { user } = useAuth();
  const nav = useNavigate();

  const isMember = user && MEMBER_ROLES.includes(user.role);
  const hasPhysical = items.some((i) => i.type === "physical");
  const subtotal = items.reduce((s, i) => s + memberPrice(i, isMember) * i.qty, 0);
  const shipping = items.filter((i) => i.type === "physical").reduce((s, i) => s + (i.shipping_price || 0) * i.qty, 0);
  const total = subtotal + shipping;
  const CART_MIN = 20;
  const belowMin = items.length > 0 && subtotal < CART_MIN;

  const checkout = () => {
    if (items.length === 0 || belowMin) return;
    setOpen(false);
    nav("/checkout");
  };

  return (
    <>
      {open && (
    <div className="fixed inset-0 z-[130] flex justify-end" data-testid="cart-drawer">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setOpen(false)} />
      <div className="relative w-full max-w-md bg-[#F9F7F3] h-full shadow-2xl flex flex-col">
        <div className="flex items-center justify-between px-6 py-5 border-b border-[#E5E0D8]">
          <h2 className="font-serif text-2xl text-[#1A242B] flex items-center gap-2"><ShoppingBag size={20} className="text-[#144F75]" /> Your cart</h2>
          <button onClick={() => setOpen(false)} data-testid="cart-close" className="text-[#4A5D6B] hover:text-[#144F75]"><X size={22} /></button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          {items.length === 0 && <p className="text-[#4A5D6B] text-sm py-10 text-center">Your cart is empty. Explore the marketplace to add items.</p>}
          {items.map((i) => (
            <div key={i.key || i.product_id} className="flex gap-3 border-b border-[#E5E0D8] pb-4" data-testid={`cart-item-${i.product_id}`}>
              {i.image && <img src={i.image} alt={i.title} className="h-16 w-16 rounded-xl object-cover shrink-0" />}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-[#1A242B] truncate">{i.title}</p>
                <p className="text-xs text-[#8FA89B] capitalize">{i.variant ? i.variant : i.type}</p>
                <div className="flex items-center gap-2 mt-2">
                  <button onClick={() => setQty(i.key || i.product_id, i.qty - 1)} data-testid={`cart-dec-${i.product_id}`} className="h-6 w-6 grid place-items-center rounded-full border border-[#E5E0D8] text-[#144F75]"><Minus size={12} /></button>
                  <span className="text-sm w-6 text-center" data-testid={`cart-qty-${i.product_id}`}>{i.qty}</span>
                  <button onClick={() => setQty(i.key || i.product_id, i.qty + 1)} data-testid={`cart-inc-${i.product_id}`} className="h-6 w-6 grid place-items-center rounded-full border border-[#E5E0D8] text-[#144F75]"><Plus size={12} /></button>
                </div>
              </div>
              <div className="text-right shrink-0">
                <p className="text-sm font-serif text-[#144F75]">${(memberPrice(i, isMember) * i.qty).toFixed(2)}</p>
                <button onClick={() => remove(i.key || i.product_id)} data-testid={`cart-remove-${i.product_id}`} className="text-red-500 mt-2"><Trash2 size={15} /></button>
              </div>
            </div>
          ))}
        </div>

        {items.length > 0 && (
          <div className="border-t border-[#E5E0D8] px-6 py-5 space-y-2">
            <div className="flex justify-between text-sm text-[#4A5D6B]"><span>Subtotal</span><span>{money(subtotal)}</span></div>
            {hasPhysical && <div className="flex justify-between text-sm text-[#4A5D6B]"><span>Shipping (estimated)</span><span>{money(shipping)}</span></div>}
            <div className="flex justify-between font-serif text-xl text-[#1A242B]"><span>Total</span><span data-testid="cart-total">{money(total)}</span></div>
            {hasPhysical && <p className="text-[11px] text-[#8FA89B]">Final shipping & handling per seller are confirmed at checkout.</p>}
            {isMember && <p className="text-xs text-[#5C8A72]">Member pricing applied.</p>}
            {belowMin && (
              <p className="text-xs text-[#8A4B2E] bg-[#FBEEE8] border border-[#E7C3B3] rounded-lg px-3 py-2" data-testid="cart-minimum-message">
                Minimum checkout is ${CART_MIN.toFixed(2)}. Add more items to your journey to continue.
              </p>
            )}
            <button onClick={checkout} disabled={belowMin} data-testid="cart-checkout" className="eyw-btn w-full mt-3 disabled:opacity-50 disabled:cursor-not-allowed">
              Checkout
            </button>
          </div>
        )}
      </div>
    </div>
      )}
    </>
  );
}
